import type { DocsSnippets } from "./types";

/**
 * Request URLs and code samples for the docs page. Pure and client-safe: the
 * server builds every endpoint's example snippets with it, and the try-it
 * panel builds the URL it sends with the same functions, so the two can't
 * disagree about encoding.
 */

/** The environment variable every sample reads the key from. Keys never go in code or URLs. */
export const API_KEY_ENV = "STEAMPEEK_API_KEY";

export type ParamRef = { name: string; in: "path" | "query" };
export type ParamValues = Record<string, string | number | undefined>;

/** encodeURIComponent, plus `'` (which it leaves alone) so a URL is safe inside single quotes in a shell. */
export function encodePathValue(value: string): string {
  return encodeURIComponent(value).replace(/'/g, "%27");
}

/** Like a path value, but `:` and `,` stay readable (both are legal in a query string). */
export function encodeQueryValue(value: string): string {
  return encodePathValue(value).replace(/%3A/gi, ":").replace(/%2C/gi, ",");
}

function valueOf(values: ParamValues, name: string): string | number | null {
  const raw = values[name];
  if (raw === undefined || raw === null) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}

/** The template with its `{placeholders}` filled in; `missing` names the empty ones (left as-is). */
export function fillPath(template: string, values: ParamValues): { path: string; missing: string[] } {
  const missing: string[] = [];
  const path = template.replace(/\{([A-Za-z0-9_]+)\}/g, (placeholder, name: string) => {
    const value = valueOf(values, name);
    if (value === null) {
      missing.push(name);
      return placeholder;
    }
    return encodePathValue(String(value));
  });
  return { path, missing };
}

/** Non-empty query parameters, in the order the endpoint declares them. */
export function queryEntries(params: ParamRef[], values: ParamValues): [string, string | number][] {
  return params.flatMap((p): [string, string | number][] => {
    if (p.in !== "query") return [];
    const value = valueOf(values, p.name);
    return value === null ? [] : [[p.name, value]];
  });
}

export function queryString(entries: [string, string | number][]): string {
  return entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeQueryValue(String(v))}`).join("&");
}

/** Path + query string for a call. Empty values are left out (the API applies its defaults). */
export function buildRequestPath(
  template: string,
  params: ParamRef[],
  values: ParamValues,
): { path: string; missing: string[] } {
  const { path, missing } = fillPath(template, values);
  const qs = queryString(queryEntries(params, values));
  return { path: qs ? `${path}?${qs}` : path, missing };
}

export function curlSnippet(url: string): string {
  return [`curl '${url}' \\`, `  -H "Authorization: Bearer $${API_KEY_ENV}"`].join("\n");
}

function jsKey(name: string): string {
  return /^[A-Za-z_$][\w$]*$/.test(name) ? name : JSON.stringify(name);
}

export function javascriptSnippet(url: string, query: [string, string | number][]): string {
  const lines: string[] = [];
  if (query.length > 0) {
    lines.push("const params = new URLSearchParams({");
    for (const [k, v] of query) lines.push(`  ${jsKey(k)}: ${JSON.stringify(String(v))},`);
    lines.push("});");
    lines.push(`const response = await fetch(\`${url}?\${params}\`, {`);
  } else {
    lines.push(`const response = await fetch(${JSON.stringify(url)}, {`);
  }
  lines.push(`  headers: { Authorization: \`Bearer \${process.env.${API_KEY_ENV}}\` },`);
  lines.push("});");
  lines.push("const body = await response.json();");
  lines.push("if (!response.ok) throw new Error(`${body.error.code}: ${body.error.message}`);");
  lines.push("console.log(body.data, body.meta.units);");
  return lines.join("\n");
}

export function pythonSnippet(url: string, query: [string, string | number][]): string {
  const lines = ["import os", "", "import requests", "", "response = requests.get(", `    ${JSON.stringify(url)},`];
  if (query.length > 0) {
    lines.push("    params={");
    for (const [k, v] of query) lines.push(`        ${JSON.stringify(k)}: ${typeof v === "number" ? String(v) : JSON.stringify(v)},`);
    lines.push("    },");
  }
  lines.push(`    headers={"Authorization": f"Bearer {os.environ['${API_KEY_ENV}']}"},`);
  lines.push("    timeout=30,");
  lines.push(")");
  lines.push("body = response.json()");
  lines.push("if not response.ok:");
  lines.push(`    raise RuntimeError(f"{body['error']['code']}: {body['error']['message']}")`);
  lines.push(`print(body["data"], body["meta"]["units"])`);
  return lines.join("\n");
}

/** curl / fetch / requests for one call against `origin`. */
export function buildSnippets(origin: string, template: string, params: ParamRef[], values: ParamValues): DocsSnippets {
  const url = `${origin}${fillPath(template, values).path}`;
  const query = queryEntries(params, values);
  const qs = queryString(query);
  return {
    curl: curlSnippet(qs ? `${url}?${qs}` : url),
    javascript: javascriptSnippet(url, query),
    python: pythonSnippet(url, query),
  };
}

/** `claude mcp add` for the hosted server (one line: it pastes into any shell). */
export function claudeCodeMcpCommand(mcpUrl: string): string {
  return `claude mcp add --transport http steampeek ${mcpUrl} --header "Authorization: Bearer spk_…"`;
}

/** The `mcpServers` entry for clients that take a remote server's URL and headers. */
export function mcpJsonConfig(mcpUrl: string): string {
  return JSON.stringify(
    { mcpServers: { steampeek: { url: mcpUrl, headers: { Authorization: "Bearer spk_…" } } } },
    null,
    2,
  );
}

/**
 * The same server through the `mcp-remote` bridge, for clients whose config
 * only starts local (stdio) servers. mcp-remote substitutes `${…}` from `env`;
 * no space after the colon keeps the argument intact on Windows.
 */
export function mcpBridgeConfig(mcpUrl: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        steampeek: {
          command: "npx",
          args: ["-y", "mcp-remote", mcpUrl, "--header", "Authorization:${STEAMPEEK_AUTH}"],
          env: { STEAMPEEK_AUTH: "Bearer spk_…" },
        },
      },
    },
    null,
    2,
  );
}
